/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import { getCurrentSystemId } from '../../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../../share/util/individualWorkSupportUtil';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_IN_PROGRESS, executePolling } from '../../../../../share/util/api/polling';
import { apiErrorDefaultAction } from '../../../../../share/util/api/errorAction';
import ApiService from '../../../../../share/util/api/service';
import session from '../../../../../share/session';
import customProgress from "../../../../../share/components/vue/CustomProgress";
import handler from '../../../../../share/util/apiResultValidator';
import alert from '../../../../../share/alert';

//利用モジュールの取り込み
const moment = require('moment');

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',//実行監視
  EXECUTE: 'execute',//実行
  SUCCESS: 'success',//成功表示
  FAILED_ORG: 'failed_org',//失敗表示
  FAILED: 'failed'//共通エラー
};

export default {
  name: 'requirementSpADAS001',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      progress: 0,
      progressMax: 100,
      modal_special_requirement_ADAS1: false
    };
  },

  components: {
    'custom-Progress': customProgress,
  },
  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     * @returns {string} ヘッダーの色クラス
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED_ORG:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        default:
          return 'secondary';
      }
    },

    /**
     * ダイアログのヘッダータイトル
     * @returns {string} ヘッダータイトル
     */
    headerTitle() {
      const map = {
        start: 'message.common_title_confirmation',
        execute: 'individual.common_title_execution',
        success: 'message.common_title_success',
        failed: 'message.common_title_error',
        failed_org: 'message.common_title_error',
      };
      return this.$t(map[this.phase] || '');
    },

    /**
     *  ダイアログの本文テキスト
     * @returns {string} 本文テキスト
     */
    bodyText() {
      if (this.phase === 'execute') return this.executeMessage;
      const map = {
        start: `${this.i18nWsKey}.REQ_SP_WS_ADAS1_TXT_1`,
        success: `${this.i18nWsKey}.REQ_SP_WS_ADAS1_TXT_3`,
        failed_org: `${this.i18nWsKey}.REQ_SP_WS_ADAS1_TXT_4`,
        failed: `${this.i18nWsKey}.REQ_SP_WS_ADAS1_TXT_5`,
      };
      return this.$t(map[this.phase] || '');
    },

    /**
     * 画像読出し中の表示文言を作成
     * @returns {string} 実行中表示文言
     */
    executeMessage() {
      const executeMessage = this.$t(`${this.i18nWsKey}.REQ_SP_WS_ADAS1_TXT_2`);
      return executeMessage.replace('$PROGRESS$', `${this.progress}%`);
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),


    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_special_requirement_ADAS1 = false;
    },

    /**
     * 作業サポートAPIを呼び出す共通メソッド
     * @param {string} type APIタイプ ('standby'/ 'terminate')
     * @returns {Promise} 最終的な GET レスポンス
     */
    async callWspApi(type) {
      const systemId = getCurrentSystemId(this);
      const token = session.getToken();

      return await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type,
          is_special: true
        },
        () => { this.showUnexpectedError(); },
        (result) => { this.validateErrorResult(result); },
        (result) => { this.validateErrorResult(result); }
      );
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        () => { this.logout(this); },
        // セッションリセット処理のコールバック
        () => { this.resetSession(this); },
        // その他エラー発生のコールバック
        () => { this.showUnexpectedError(); },
      );
    },


    /** その他エラー発生時の表示 */
    async showUnexpectedError() {
      // すでに異常終了している場合は、二重エラー防止のため何もしない
      if (this.phase === PHASE.FAILED) {
        this.closeWizard();
        return;
      }
      //  終了処理が完了してからエラー表示
      await this.terminateProcess();
      this.phase = PHASE.FAILED;
    },

    /** カメラ画像出力 YES */
    async standbyProcess() {
      try {
        this.isLoading = true;
        const response = await this.callWspApi('standby');

        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              this.readImageProcess();
              break;
            default:
              await this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 画像読出し */
    async readImageProcess() {
      this.phase = PHASE.EXECUTE;
      this.progress = 0;

      const systemId = getCurrentSystemId(this);
      const token = session.getToken();
      const path = `/netApps/${this.netAppId}/systems/${systemId}/workSupports/${this.workSupportId}`;

      // ポーリング実行時に毎回レスポンス内の progress の確認を行うため、executePolling を使用してポーリング処理
      const response = await executePolling(
        this,
        // POST API コールバック
        async () => {
          return await ApiService.postCall(
            ApiService.WSP,
            path,
            {
              id_token: token.id_token,
              type: 'read',
              is_special: true
            }
          );
        },
        // GET API コールバック
        async (postApiResponse) => {
          return await ApiService.getCall(
            ApiService.WSP,
            `${path}/requests/${postApiResponse.data.request_id}`
          );
        },
        // エラー発生コールバック
        async () => { await this.showUnexpectedError(); },
        // POST レスポンスチェックコールバック（デフォルト使用）
        undefined,
        // GET レスポンスチェックコールバック
        (getApiResponse) => {
          // status に応じてポーリング継続/完了を判定し、progress を更新する
          const newProgress = getApiResponse.data?.user_data?.progress || null;
          switch (getApiResponse.data.status) {
            case REQUEST_STATUS_IN_PROGRESS:
              // CCからIoTメッセージが返ってくるまで use_data が入らないため その場合は progress は初期値のまま
              this.progress = newProgress ? newProgress : this.progress;
              return REQUEST_STATUS_IN_PROGRESS;
            case REQUEST_STATUS_COMPLETED:
              this.progress = this.progressMax;
              return REQUEST_STATUS_COMPLETED;
            default:
              // completed、in_progress以外（failed など）は完了扱いでポーリング終了
              return REQUEST_STATUS_COMPLETED;
          }
        },
        // POST エラー結果コールバック
        (result) => { this.validateErrorResult(result); },
        // GET エラー結果コールバック
        (result) => { this.validateErrorResult(result); },
        // タイムアウト停止コールバック（デフォルト使用）
        undefined,
        // ポーリングリトライ回数
        process.env.VUE_APP_WORK_SUPPORT_RETRY_COUNT,
        // ポーリングリトライ間隔
        process.env.VUE_APP_WORK_SUPPORT_RETRY_INTERVAL
      );

      if (response) {
        // 成功/失敗どちらでも終了処理を行う
        await this.terminateProcess();
        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            this.fileOutput(response.data.user_data.file_path);
            break;
          default:
            this.phase = PHASE.FAILED_ORG;
            break;
        }
      }
    },

    /** 終了処理 */
    async terminateProcess() {
      try {
        this.isLoading = true;
        await this.callWspApi('terminate');

      } finally {
        this.isLoading = false;
      }
    },

    /**
     * ファイル出力
     * @param {string} filePath 画像読出しで取得したファイルパス
     */
    async fileOutput(filePath) {
      this.isLoading = true;
      try {
        // ダウンロードURL処理
        const formattedDate = moment().format("YYYYMMDDHHmmss");
        const extension = filePath.slice(filePath.lastIndexOf('.')); //「.」付き拡張子を取り出す
        const fileName = `${formattedDate}${extension}`; //ファイル名を生成
        await individualWorkSupportUtil.downloadFile(filePath, fileName);
        // ダウンロード成功後、成功画面を表示
        this.phase = PHASE.SUCCESS;
      } catch (error) {
        if (error && error.behaviorType === handler.behaviorTypes.logout) {
          // ユーザー権限エラーの場合、エラーダイアログを表示しログアウトする
          alert.showError(this, null, error.message, () => {
            this.logout(this);
            return;
          });
        }
        this.phase = PHASE.FAILED_ORG;
      } finally {
        this.isLoading = false;
      }
    },
  },

  mounted() {
    this.modal_special_requirement_ADAS1 = true;
  },
};